import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { Icons } from "../../lib/Icons";
import ResourcePackDetailModal from "../../components/ResourcePackDetailModal";

// Category configuration with icons and colors
const CATEGORIES = [
  {
    id: 'all',
    label: 'All Resources',
    Icon: Icons.Squares2x2,
    gradient: 'from-slate-500 to-slate-600',
  },
  {
    id: 'transition',
    label: 'Transition',
    Icon: Icons.ArrowsRightLeft,
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    id: 'emotional-regulation',
    label: 'Emotional Regulation',
    Icon: Icons.Heart,
    gradient: 'from-orange-500 to-red-500',
  },
  {
    id: 'sensory',
    label: 'Sensory',
    Icon: Icons.Sparkles,
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    id: 'motor-skills',
    label: 'Motor Skills',
    Icon: Icons.Hand,
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    id: 'communication',
    label: 'Communication',
    Icon: Icons.ChatBubble,
    gradient: 'from-cyan-500 to-blue-500',
  },
];

// Map icon names to components
const ICON_MAP = {
  ArrowsRightLeft: Icons.ArrowsRightLeft,
  Fire: Icons.Fire || Icons.ExclamationTriangle,
  Hand: Icons.Hand,
  Sparkles: Icons.Sparkles,
  Eye: Icons.Eye,
  Heart: Icons.Heart,
  ChatBubble: Icons.ChatBubble,
  Document: Icons.Document,
};

// Fallback static packs for when API fails (for unauthenticated users)
const FALLBACK_PACKS = [
  {
    id: 'transitions-primary',
    title: 'Transitions Support Pack - Primary',
    description: 'Visual schedules, now/next boards, and transition countdown tools for primary-aged pupils.',
    category: 'transition',
    worksheetCount: 12,
    tags: ['visual schedules', 'transitions', 'primary', 'autism-friendly'],
    gradient: 'from-blue-500 to-cyan-500',
    thumbnailIcon: 'ArrowsRightLeft',
    isPremium: true,
    phase: 'primary',
  },
  {
    id: 'emotional-regulation-pack',
    title: 'Emotional Regulation Toolkit',
    description: 'Zones of Regulation worksheets, emotion cards, calming strategies, and self-regulation logs.',
    category: 'emotional-regulation',
    worksheetCount: 18,
    tags: ['emotions', 'self-regulation', 'zones', 'SEMH'],
    gradient: 'from-orange-500 to-red-500',
    thumbnailIcon: 'Heart',
    isPremium: true,
    phase: 'primary',
  },
  {
    id: 'sensory-diet-pack',
    title: 'Sensory Diet Activities',
    description: 'Sensory breaks, movement activities, calming techniques, and sensory checklists for the classroom.',
    category: 'sensory',
    worksheetCount: 15,
    tags: ['sensory processing', 'OT', 'sensory breaks', 'movement'],
    gradient: 'from-purple-500 to-pink-500',
    thumbnailIcon: 'Sparkles',
    isPremium: true,
    phase: 'primary',
  },
  {
    id: 'fine-motor-skills',
    title: 'Fine Motor Skills Development',
    description: 'Handwriting warm-ups, pencil grip activities, cutting practice, and coordination exercises.',
    category: 'motor-skills',
    worksheetCount: 14,
    tags: ['fine motor', 'handwriting', 'OT', 'coordination'],
    gradient: 'from-green-500 to-emerald-500',
    thumbnailIcon: 'Hand',
    isPremium: true,
    phase: 'primary',
  },
  {
    id: 'communication-boards',
    title: 'Communication Boards & PECS',
    description: 'Choice boards, request cards, visual communication supports, and core vocabulary boards.',
    category: 'communication',
    worksheetCount: 20,
    tags: ['AAC', 'PECS', 'visual supports', 'communication'],
    gradient: 'from-cyan-500 to-blue-500',
    thumbnailIcon: 'ChatBubble',
    isPremium: true,
    phase: 'primary',
  },
  {
    id: 'anxiety-toolkit',
    title: 'Anxiety Management Toolkit',
    description: 'Worry worksheets, grounding techniques, relaxation scripts, and anxiety tracking tools.',
    category: 'emotional-regulation',
    worksheetCount: 16,
    tags: ['anxiety', 'mental health', 'wellbeing', 'relaxation'],
    gradient: 'from-rose-500 to-pink-500',
    thumbnailIcon: 'Heart',
    isPremium: true,
    phase: 'secondary',
  },
];

const ResourcesPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resourcePacks, setResourcePacks] = useState([]);
  const [kbItems, setKbItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedResourceType, setSelectedResourceType] = useState('all'); // all, resource-pack, activity, lesson-idea, strategy
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [downloadingPackId, setDownloadingPackId] = useState(null);
  const [downloadingItemId, setDownloadingItemId] = useState(null);
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [packDetails, setPackDetails] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        // Check subscription
        try {
          const functions = getFunctions(undefined, 'europe-west3');
          const checkSub = httpsCallable(functions, 'checkSoloSubscriptionFunc');
          const result = await checkSub();
          setSubscriptionStatus(result.data);
        } catch (e) {
          console.error('Subscription check failed:', e);
          setSubscriptionStatus({ hasActiveSubscription: false });
        }

        // Load download history
        try {
          const functions = getFunctions(undefined, 'europe-west3');
          const getHistory = httpsCallable(functions, 'getResourcePackDownloadHistoryFunc');
          const historyResult = await getHistory();
          setDownloadHistory(historyResult.data.downloads || []);
        } catch (e) {
          console.error('Failed to load download history:', e);
        }
      }

      // Load resources (available to all)
      await loadResources();
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loadResources = async () => {
    try {
      // Load resource packs
      const functions = getFunctions(undefined, 'europe-west3');
      const listPacks = httpsCallable(functions, 'listResourcePacksFunc');
      const packsResult = await listPacks({});
      const apiPacks = packsResult.data.packs || [];
      setResourcePacks(apiPacks.length > 0 ? apiPacks : FALLBACK_PACKS);

      // Load KB items
      const kbRef = collection(db, 'kb_items');
      const kbSnapshot = await getDocs(kbRef);
      const allKbItems = [];
      kbSnapshot.forEach(doc => {
        allKbItems.push({
          id: doc.id,
          ...doc.data(),
          resourceType: 'kb-item', // Mark as KB item
        });
      });
      setKbItems(allKbItems);
    } catch (error) {
      console.error('Error loading resources:', error);
      // Use fallback packs when API fails
      setResourcePacks(FALLBACK_PACKS);
      setKbItems([]);
    }
  };

  const handleViewPack = async (packId) => {
    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const getPack = httpsCallable(functions, 'getResourcePackFunc');
      const result = await getPack({ packId });
      
      setPackDetails(result.data.pack);
      setSelectedPack(resourcePacks.find(p => p.id === packId));
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load pack details:', error);
      alert('Failed to load pack details. Please try again.');
    }
  };

  const handleDownload = async (packId) => {
    if (!user) {
      navigate('/signup/solo');
      return;
    }

    if (!subscriptionStatus?.hasActiveSubscription) {
      navigate('/solo/checkout');
      return;
    }

    setDownloadingPackId(packId);

    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const download = httpsCallable(functions, 'downloadResourcePackFunc');
      const result = await download({ packId });

      // Trigger download
      const link = document.createElement('a');
      link.href = result.data.downloadUrl;
      link.download = result.data.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Update download history
      const pack = resourcePacks.find(p => p.id === packId);
      if (pack) {
        setDownloadHistory(prev => [
          { packId, packSlug: pack.slug, packTitle: pack.title, downloadedAt: new Date().toISOString() },
          ...prev,
        ]);
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert(error.message || 'Failed to download resource pack. Please try again.');
    } finally {
      setDownloadingPackId(null);
    }
  };

  const handleDownloadItem = async (packId, itemId) => {
    if (!user) {
      navigate('/signup/solo');
      return;
    }

    if (!subscriptionStatus?.hasActiveSubscription) {
      navigate('/solo/checkout');
      return;
    }

    setDownloadingItemId(itemId);

    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const downloadItem = httpsCallable(functions, 'downloadPackItemFunc');
      const result = await downloadItem({ packId, itemId });

      // Trigger download
      const link = document.createElement('a');
      link.href = result.data.downloadUrl;
      link.download = result.data.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Item download failed:', error);
      alert(error.message || 'Failed to download item. Please try again.');
    } finally {
      setDownloadingItemId(null);
    }
  };

  // Determine resource type for KB items
  const getResourceType = (item) => {
    if (item.isIntervention) return 'intervention';
    if (item.activityType === 'structured') return 'activity';
    // Could add logic to distinguish lesson-idea vs strategy based on tags/description
    if (item.tags?.some(t => t.toLowerCase().includes('lesson') || t.toLowerCase().includes('teaching'))) {
      return 'lesson-idea';
    }
    if (item.tags?.some(t => t.toLowerCase().includes('strategy') || t.toLowerCase().includes('support'))) {
      return 'strategy';
    }
    return 'activity'; // Default
  };

  // Combine resource packs and KB items
  const allResources = [
    ...resourcePacks.map(pack => ({ ...pack, resourceType: 'resource-pack', isResourcePack: true })),
    ...kbItems.map(item => ({ ...item, resourceType: getResourceType(item), isResourcePack: false })),
  ];

  // Filter by category
  let filtered = selectedCategory === 'all'
    ? allResources
    : allResources.filter(resource => {
        if (resource.isResourcePack) {
          return resource.category === selectedCategory;
        } else {
          // Map KB item targetDomains to categories
          const domainToCategory = {
            'Communication & Interaction': 'communication',
            'SEMH': 'emotional-regulation',
            'Sensory & Physical': 'sensory',
            'Cognition & Learning': 'motor-skills', // Approximate mapping
          };
          return resource.targetDomains?.some(domain => {
            const category = domainToCategory[domain] || selectedCategory;
            return category === selectedCategory;
          });
        }
      });

  // Filter by resource type
  if (selectedResourceType !== 'all') {
    filtered = filtered.filter(resource => resource.resourceType === selectedResourceType);
    // Interventions: show umbrella + other interventions, hide child stages (e.g. Attention Autism stages)
    if (selectedResourceType === 'intervention') {
      filtered = filtered.filter(resource => !resource.parentInterventionSlug);
    }
  } else {
    // "All" view: hide umbrella cards (they only appear under Interventions filter)
    filtered = filtered.filter(resource => !resource.interventionUmbrella);
  }

  const filteredResources = filtered;

  const hasDownloaded = (resourceId) => {
    return downloadHistory.some(d => d.packId === resourceId);
  };

  const handleViewResource = async (resource) => {
    if (resource.isResourcePack) {
      // Handle resource pack
      await handleViewPack(resource.id);
    } else {
      // Handle KB item - navigate to detail page
      navigate(`/solo/activity/${resource.id}`);
    }
  };

  const handleDownloadResource = async (resource) => {
    if (resource.isResourcePack) {
      // Handle resource pack download
      await handleDownload(resource.id);
    } else {
      // KB items don't have full pack downloads, but may have downloadable resources
      // Navigate to detail page where they can download individual resources
      navigate(`/solo/activity/${resource.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/solo')}
                className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <Icons.ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <img
                src="/brand/White logo on purple.png"
                alt="SEN-Ed"
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-white">Resources</span>
            </div>

            {user && !subscriptionStatus?.hasActiveSubscription && (
              <button
                onClick={() => navigate('/solo/checkout')}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all"
              >
                Unlock All Packs
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 md:py-16">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 via-slate-900 to-pink-900/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Icons.FolderOpen className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Teaching Resources
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Browse lesson ideas, activities, strategies, and downloadable resource packs. Find the perfect resource for your students' needs.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-4">
            {CATEGORIES.map(category => {
              const CategoryIcon = category.Icon;
              const isSelected = selectedCategory === category.id;

              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${isSelected
                    ? `bg-gradient-to-r ${category.gradient} text-white shadow-lg`
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                >
                  <CategoryIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">{category.label}</span>
                  {category.id !== 'all' && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-white/20' : 'bg-slate-700'
                      }`}>
                      {allResources.filter(r => {
                        if (r.isResourcePack) return r.category === category.id;
                        // Map KB item domains to categories
                        const domainToCategory = {
                          'Communication & Interaction': 'communication',
                          'SEMH': 'emotional-regulation',
                          'Sensory & Physical': 'sensory',
                          'Cognition & Learning': 'motor-skills',
                        };
                        return r.targetDomains?.some(d => domainToCategory[d] === category.id);
                      }).length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Resource Type Filter */}
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { id: 'all', label: 'All Types', Icon: Icons.Squares2x2 },
              { id: 'resource-pack', label: 'Resource Packs', Icon: Icons.FolderOpen },
              { id: 'activity', label: 'Activities', Icon: Icons.ClipboardDocumentList },
              { id: 'lesson-idea', label: 'Lesson Ideas', Icon: Icons.BookOpen },
              { id: 'strategy', label: 'Strategies', Icon: Icons.LightBulb },
            ].map(type => {
              const TypeIcon = type.Icon;
              const isSelected = selectedResourceType === type.id;

              return (
                <button
                  key={type.id}
                  onClick={() => setSelectedResourceType(type.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${isSelected
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                >
                  <TypeIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">{type.label}</span>
                  {type.id !== 'all' && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-white/20' : 'bg-slate-700'
                      }`}>
                      {allResources.filter(r => r.resourceType === type.id).length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredResources.length === 0 ? (
            <div className="text-center py-12">
              <Icons.FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No resources in this category</h3>
              <p className="text-slate-400">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map(resource => {
                // Handle resource packs
                if (resource.isResourcePack) {
                  const pack = resource;
                const PackIcon = ICON_MAP[pack.thumbnailIcon] || Icons.Document;
                const isDownloading = downloadingPackId === pack.id;
                const downloaded = hasDownloaded(pack.id);
                const categoryInfo = CATEGORIES.find(c => c.id === pack.category);

                return (
                  <div
                    key={pack.id}
                    className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-purple-500/50 transition-all group"
                  >
                    {/* Pack Header with Gradient */}
                    <div className={`h-32 bg-gradient-to-br ${pack.gradient} p-5 flex flex-col justify-between relative`}>
                      {/* Phase Badge */}
                      {pack.phase && (
                        <span className="absolute top-3 right-3 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                          {pack.phase === 'primary' ? 'Primary' : 'Secondary'}
                        </span>
                      )}

                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <PackIcon className="w-6 h-6 text-white" />
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white">
                          {pack.worksheetCount} worksheets
                        </span>
                        {downloaded && (
                          <span className="px-2 py-1 bg-green-500/30 backdrop-blur-sm rounded-full text-xs text-green-200 flex items-center gap-1">
                            <Icons.Check className="w-3 h-3" />
                            Downloaded
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Pack Content */}
                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">
                        {pack.title}
                      </h3>
                      <p className="text-slate-400 text-sm mb-4 line-clamp-3">
                        {pack.description}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {pack.tags.slice(0, 4).map(tag => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-slate-700/50 text-slate-400 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {pack.tags.length > 4 && (
                          <span className="px-2 py-0.5 text-slate-500 text-xs">
                            +{pack.tags.length - 4} more
                          </span>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewPack(pack.id)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-all"
                        >
                          <Icons.Eye className="w-4 h-4" />
                          View Contents
                        </button>
                        
                        {pack.isPremium && !subscriptionStatus?.hasActiveSubscription ? (
                          <button
                            onClick={() => user ? navigate('/solo/checkout') : navigate('/signup/solo')}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all"
                          >
                            <Icons.LockClosed className="w-4 h-4" />
                            Subscribe
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDownload(pack.id)}
                            disabled={isDownloading}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${isDownloading
                              ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                              : downloaded
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-purple-600 hover:bg-purple-700 text-white'
                              }`}
                          >
                            {isDownloading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                                Preparing...
                              </>
                            ) : downloaded ? (
                              <>
                                <Icons.ArrowDownTray className="w-4 h-4" />
                                Download
                              </>
                            ) : (
                              <>
                                <Icons.ArrowDownTray className="w-4 h-4" />
                                Download
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
                } else {
                  // Handle KB items
                  const item = resource;
                  const isIntervention = item.isIntervention === true;
                  const resourceTypeLabel = {
                    'activity': 'Activity',
                    'lesson-idea': 'Lesson Idea',
                    'strategy': 'Strategy',
                    'intervention': 'Intervention',
                  }[item.resourceType] || 'Resource';

                  // Determine icon based on targetDomains
                  const getDomainIcon = (domains) => {
                    if (domains?.includes('Communication & Interaction')) return Icons.ChatBubble;
                    if (domains?.includes('SEMH')) return Icons.Heart;
                    if (domains?.includes('Sensory & Physical')) return Icons.Sparkles;
                    if (domains?.includes('Cognition & Learning')) return Icons.AcademicCap;
                    return Icons.Document;
                  };

                  const ItemIcon = getDomainIcon(item.targetDomains);
                  const categoryInfo = CATEGORIES.find(c => {
                    const domainToCategory = {
                      'Communication & Interaction': 'communication',
                      'SEMH': 'emotional-regulation',
                      'Sensory & Physical': 'sensory',
                      'Cognition & Learning': 'motor-skills',
                    };
                    return item.targetDomains?.some(d => domainToCategory[d] === c.id);
                  });

                  return (
                    <div
                      key={item.id}
                      className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-purple-500/50 transition-all group cursor-pointer"
                      onClick={() => navigate(`/solo/activity/${item.id}`)}
                    >
                      {/* Item Header */}
                      <div className={`h-32 bg-gradient-to-br ${categoryInfo?.gradient || 'from-slate-600 to-slate-700'} p-5 flex flex-col justify-between relative`}>
                        {/* Badges */}
                        <div className="absolute top-3 right-3 flex gap-2">
                          {isIntervention && (
                            <span className="px-2 py-1 bg-amber-500/30 backdrop-blur-sm rounded-full text-xs text-amber-200 font-medium">
                              Intervention
                            </span>
                          )}
                          <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white">
                            {resourceTypeLabel}
                          </span>
                        </div>

                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                          <ItemIcon className="w-6 h-6 text-white" />
                        </div>

                        <div className="flex items-center gap-2">
                          {item.recommendedFrequency && (
                            <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white">
                              {item.recommendedFrequency.sessionMinutes} min
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Item Content */}
                      <div className="p-5">
                        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-slate-400 text-sm mb-4 line-clamp-3">
                          {item.description}
                        </p>

                        {/* Tags */}
                        {item.tags && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {item.tags.slice(0, 4).map(tag => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 bg-slate-700/50 text-slate-400 text-xs rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                            {item.tags.length > 4 && (
                              <span className="px-2 py-0.5 text-slate-500 text-xs">
                                +{item.tags.length - 4} more
                              </span>
                            )}
                          </div>
                        )}

                        {/* Action Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/solo/activity/${item.id}`);
                          }}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-all"
                        >
                          <Icons.Eye className="w-4 h-4" />
                          View Resource
                        </button>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )}
        </div>
      </section>

      {/* Download History (for subscribers) */}
      {user && subscriptionStatus?.hasActiveSubscription && downloadHistory.length > 0 && (
        <section className="py-12 bg-slate-800/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Icons.Clock className="w-5 h-5 text-slate-400" />
              Recent Downloads
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {downloadHistory.slice(0, 6).map((download, idx) => {
                const pack = resourcePacks.find(p => p.id === download.packId);
                if (!pack) return null;
                
                return (
                  <div
                    key={`${download.packId}-${idx}`}
                    className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl"
                  >
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <Icons.Check className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{download.packTitle}</p>
                      <p className="text-slate-500 text-sm">
                        {new Date(download.downloadedAt).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDownload(download.packId)}
                      className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      <Icons.ArrowDownTray className="w-5 h-5 text-slate-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA for non-subscribers */}
      {!subscriptionStatus?.hasActiveSubscription && (
        <section className="py-12 bg-gradient-to-r from-purple-900/50 to-pink-900/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Unlock All Resources
            </h2>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Get unlimited access to all resources, resource packs, symbols, the resource builder, learning profiles, and more.
            </p>
            <button
              onClick={() => user ? navigate('/solo/checkout') : navigate('/signup/solo')}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all text-lg"
            >
              {user ? 'Start 7-Day Free Trial' : 'Sign Up for Free Trial'}
            </button>
          </div>
        </section>
      )}

      {/* Pack Detail Modal */}
      <ResourcePackDetailModal
        pack={packDetails || selectedPack}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setPackDetails(null);
          setSelectedPack(null);
        }}
        onDownloadPack={handleDownload}
        onDownloadItem={handleDownloadItem}
        subscriptionStatus={subscriptionStatus}
        user={user}
        downloadingPackId={downloadingPackId}
        downloadingItemId={downloadingItemId}
      />
    </div>
  );
};

export default ResourcesPage;
